import React, { useContext, useState, useEffect } from 'react';
import {
    AppBar,
    Toolbar,
    Button,
    Badge,
    Box,
    Container,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Snackbar,
    Typography
} from '@mui/material';
import '../../styles/appStyles.css';
import MuiLink from '@mui/material/Link';
import { AttachMoney, HomeRounded, ShoppingCart } from '@mui/icons-material';
import { Link as RouterLink, Outlet } from 'react-router-dom';
import AppContext from '../../AppContext';

const MainLayout = () => {
    const { cart, selectedItems, orders, code } = useContext(AppContext);
    const baseUrl = process.env.REACT_APP_BACKEND_BASE_URL;
    const [dialogOpen, setDialogOpen] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const acceptedOrders = orders.filter(order => order.status === 'ACCEPTED');

    const callWaiter = async () => {
        try {
            const encodedCode = encodeURIComponent(code);
            await fetch(`${baseUrl}/api/customers/callWaiter?code=${encodedCode}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            });
        } catch (error) {
            alert('Wystąpił błąd podczas wezwania kelnera.');
        }
    };

    const handleOpenDialog = () => setDialogOpen(true);
    const handleCloseDialog = () => setDialogOpen(false);
    const handleConfirmCall = () => {
        callWaiter();
        handleCloseDialog();
        setSnackbarOpen(true);
    };
            const [scrolled, setScrolled] = useState(false);

            useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
            }, []);

            return (
        <Box className="app-container">
            <AppBar 
                position="fixed" 
                className="custom-navbar" 
                sx={{
                    height: scrolled ? '55px' : '60px',
                    background: 'linear-gradient(135deg, var(--primary-color), var(--secondary-color))',
                    transition: 'all 0.3s ease'
                }}>
                <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', p: { xs: '0 8px', sm: '0 16px' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Button 
                            component={RouterLink} 
                            to="/" 
                            className="nav-button"
                            sx={{ color: 'white', minWidth: '40px', p: { xs: '6px 8px', sm: '6px 12px' } }}>
                            <HomeRounded />
                        </Button>
                        <Button 
                            component={RouterLink} 
                            to="/menu" 
                            className="nav-button"
                            sx={{ 
                                color: 'white', 
                                fontWeight: 600, 
                                letterSpacing: '1px',
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                p: { xs: '4px 8px', sm: '6px 12px' }
                            }}>
                            Menu
                        </Button>
                        <Button 
                            onClick={handleOpenDialog} 
                            className="nav-button waiter-button"
                            sx={{ 
                                background: 'var(--accent-color)',
                                color: 'var(--dark-color)',
                                '&:hover': {
                                    background: 'var(--accent-color)',
                                    filter: 'brightness(1.1)'
                                },
                                fontSize: { xs: '0.7rem', sm: '0.875rem' },
                                p: { xs: '3px 6px', sm: '6px 12px' },
                                whiteSpace: 'nowrap',
                                ml: { xs: '2px', sm: '4px' }
                            }}>
                            Wezwij kelnera
                        </Button>
                    </Box>

                    <Box display="flex" justifyContent="flex-end">
                        {acceptedOrders.length > 0 && selectedItems.length > 0 && (
                            <Button 
                                component={RouterLink} 
                                to="/payment" 
                                className="nav-button badge"
                                sx={{ minWidth: '40px', p: { xs: '6px 8px', sm: '6px 12px' } }}
                            >
                                <Badge 
                                    badgeContent={selectedItems.length} 
                                    color="error"
                                    sx={{ 
                                        '& .MuiBadge-badge': { 
                                            background: 'var(--accent-color)',
                                            color: 'var(--dark-color)',
                                            fontWeight: 'bold'
                                        } 
                                    }}>
                                    <AttachMoney sx={{ color: 'white' }} />
                                </Badge>
                            </Button>
                        )}
                        {cart.length > 0 && (
                            <Button 
                                component={RouterLink} 
                                to="/cart" 
                                className="nav-button badge"
                                sx={{ minWidth: '40px', p: { xs: '6px 8px', sm: '6px 12px' } }}
                            >
                                <Badge 
                                    badgeContent={cart.length} 
                                    color="error"
                                    sx={{ 
                                        '& .MuiBadge-badge': { 
                                            background: 'var(--accent-color)',
                                            color: 'var(--dark-color)',
                                            fontWeight: 'bold'
                                        } 
                                    }}>
                                    <ShoppingCart sx={{ color: 'white' }} />
                                </Badge>
                            </Button>
                        )}
                    </Box>
                </Toolbar>
            </AppBar>

            <Box component="main" className="main-content" sx={{ flexGrow: 1, pt: 10, pb: 2 }}>
                <Container maxWidth="lg">
                    <Outlet />
                </Container>
            </Box>

            <Box component="footer" className="custom-footer">
                <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                    Menu Mate 2025{' '}
                    <MuiLink 
                        component={RouterLink} 
                        to="/contact" 
                        className="footer-link">
                        Kontakt
                    </MuiLink>
                </Typography>
            </Box>

            <Dialog 
                open={dialogOpen} 
                onClose={handleCloseDialog}
                PaperProps={{ 
                    className: "custom-dialog",
                    sx: { borderRadius: '20px', overflow: 'hidden' }
                }}
            >
                <DialogTitle className="dialog-title">
                    Czy na pewno chcesz wezwać kelnera?
                </DialogTitle>
                <DialogContent className="dialog-content" />
                <DialogActions className="dialog-actions">
                    <Button 
                        onClick={handleCloseDialog}
                        sx={{ 
                            borderRadius: '50px', 
                            px: 3,
                            color: 'var(--dark-color)',
                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' } 
                        }}
                    >
                        Anuluj
                    </Button>
                    <Button 
                        onClick={handleConfirmCall} 
                        autoFocus
                        className="action-button primary-button"
                        sx={{ 
                            borderRadius: '50px', 
                            px: 3
                        }}
                    >
                        Tak
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                onClose={() => setSnackbarOpen(false)}
            >
                <Alert 
                    onClose={() => setSnackbarOpen(false)} 
                    severity="success"
                    className="toast-notification"
                    icon={false}
                    sx={{ 
                        background: 'linear-gradient(135deg, var(--success-color), #7bed9f)',
                        color: 'white',
                        fontWeight: 'bold',
                        px: 3,
                        py: 1.5,
                        fontSize: '1rem'
                    }}
                >
                    🔔 Kelner został wezwany!
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default MainLayout;
